/**
 * V2Edge.jsx — Custom React Flow edge that honours ELK's bend points and
 * drops a branch-label pill for switch / fork branches.
 *
 * ELK's ORTHOGONAL routing gives us a polyline of {x,y} points per edge
 * (startPoint + bendPoints + endPoint). React Flow's built-in edge types
 * (`smoothstep`, `step`, etc.) recompute their own routes and ignore ELK's
 * geometry, which re-introduces the edges-through-nodes artefact we fought
 * for two tasks. This edge paints ELK's polyline verbatim.
 *
 * Branch labels (non-'default' case names and fork branch names) render as
 * a pill at the midpoint of the polyline, with a subtle drop-shadow so
 * they read cleanly against container chrome and grid backgrounds.
 */

import React from 'react';
import { BaseEdge, EdgeLabelRenderer, getSmoothStepPath, Position } from '@xyflow/react';

export default function V2Edge(props) {
  const { id, data, style, sourceX, sourceY, targetX, targetY } = props;
  const bendPoints = data?.bendPoints || [];

  /* G267 — Cognitive cross-cutting edges (loop ⇄ tool catalog).
   *
   * These are emitted with NO bendPoints: ELK never routes them (their tool endpoints are
   * pinned post-layout, off the structural trunk), so they fell through to `straightPath` —
   * a raw diagonal that cuts straight THROUGH the Cognitive Loop box and collides with the
   * other fan-out edges. Route them orthogonally with rounded corners instead, so the tool
   * catalog reads as a clean bus off the side of the loop rather than a starburst over it.
   *
   * Each edge gets its own lane offset so N availability edges (and their returns) don't
   * stack on the same polyline. Availability leaves the loop's RIGHT side; the return comes
   * back into its LEFT — which also makes the direction of travel obvious at a glance. */
  const xcVariant = data?.variant;
  const isAvailability = xcVariant === 'availability';
  const isCognitiveReturn = xcVariant === 'cognitiveReturn';
  const isCrossCutting = (isAvailability || isCognitiveReturn) && bendPoints.length < 2;
  let smoothPath = null;
  if (isCrossCutting) {
    /* G267c — There is now exactly ONE cross-cutting edge per cognitive loop: loop → tool
     * catalog GROUP. Both endpoints have dedicated side handles (`cog-out` on the loop's
     * right, `cog-in` on the group's left) and V2CanvasView centres the group vertically on
     * the loop, so this is a short, level, soft-cornered run with nothing to route around.
     * No lanes, no corridors — those existed only to keep N per-tool routes apart, and
     * there is no longer an N. */
    const [p] = getSmoothStepPath({
      sourceX, sourceY, targetX, targetY,
      sourcePosition: Position.Right,
      targetPosition: Position.Left,
      borderRadius: 14,   // soft corners
      offset: 20,
    });
    smoothPath = p;
  }
  // Render ELK's pre-computed polyline as authoritative. The earlier
  // attempt at "stale route" detection (compare polyline endpoints vs
  // live source/target handle positions, fall back to straight on
  // divergence) misfired because ReactFlow's handle-position vs
  // ELK's startPoint/endPoint normally differ by 30-60px even with
  // no drag — handles sit at the connector edge of a node while ELK
  // anchors at the node's bounding side-mid. The 24px threshold
  // flagged every edge as stale and degraded ALL routes to straight
  // lines (the "criss-cross" regression). For now we accept that
  // dragging a node will visually disconnect its edges until the
  // next layout pass; the proper fix is incremental ELK rerouting
  // on drag-end, scheduled post-launch.
  const path = bendPoints.length >= 2
    ? polylinePath(bendPoints)
    : (smoothPath || straightPath(sourceX, sourceY, targetX, targetY));

  const labelText = data?.labelText || data?.branchLabel;
  /* Cross-cutting edges carry NO label pill. The tool card's own MAY INVOKE badge says it,
   * and the pills were landing directly on top of that badge. */
  const showLabel = !isCrossCutting
    && labelText && labelText !== 'default' && labelText !== 'out';
  const labelPos = data?.labelPos || midpoint(bendPoints, sourceX, sourceY, targetX, targetY);
  const isDefault = !!data?.isDefault;
  const isConvergence = data?.variant === 'convergence';
  const isLoopBack = data?.variant === 'loopBack';

  /* Marker selection.
   *   - loopBack   → orange arrow, so the iteration direction reads at a glance.
   *   - crossCutting → cyan arrows on ONE or BOTH ends. A tool the loop may invoke AND that
   *     returns into it gets a double-headed edge (loop ⇄ tool) — that is what replaces the
   *     old pair of overlapping polylines.
   *   - convergence → no arrow. */
  let markerEnd;
  let markerStart;
  if (isCrossCutting) {
    if (data?.arrowForward !== false) markerEnd = 'url(#v2-arrow-cognitive)';
    if (data?.arrowBack) markerStart = 'url(#v2-arrow-cognitive)';
  } else if (!isConvergence) {
    markerEnd = isLoopBack ? 'url(#v2-arrow-loop)' : 'url(#v2-arrow)';
  }

  return (
    <>
      <BaseEdge
        id={id}
        path={path}
        style={style}
        markerEnd={markerEnd}
        markerStart={markerStart}
      />
      {showLabel && labelPos && (
        <EdgeLabelRenderer>
          <div
            style={{
              position: 'absolute',
              transform: `translate(-50%, -50%) translate(${labelPos.x}px,${labelPos.y}px)`,
              padding: '2px 8px',
              fontSize: 10, fontWeight: 700,
              textTransform: 'uppercase', letterSpacing: 0.4,
              color: isDefault ? '#475569' : '#7c2d12',
              background: isDefault ? '#e2e8f0' : '#fff7ed',
              border: `1px solid ${isDefault ? '#94a3b8' : '#fdba74'}`,
              borderRadius: 999,
              pointerEvents: 'none',
              whiteSpace: 'nowrap',
              boxShadow: '0 1px 2px rgba(15,23,42,0.08)',
            }}
          >
            {String(labelText)}
          </div>
        </EdgeLabelRenderer>
      )}
    </>
  );
}

function polylinePath(points) {
  if (!points || points.length === 0) return '';
  const head = points[0];
  const tail = points.slice(1);
  return `M ${head.x} ${head.y} ${tail.map((p) => `L ${p.x} ${p.y}`).join(' ')}`;
}

function straightPath(x1, y1, x2, y2) {
  return `M ${x1} ${y1} L ${x2} ${y2}`;
}

function midpoint(points, sx, sy, tx, ty) {
  if (points && points.length >= 2) {
    const mid = Math.floor(points.length / 2);
    if (points.length % 2 === 1) {
      return { x: points[mid].x, y: points[mid].y };
    }
    return {
      x: (points[mid - 1].x + points[mid].x) / 2,
      y: (points[mid - 1].y + points[mid].y) / 2,
    };
  }
  return { x: (sx + tx) / 2, y: (sy + ty) / 2 };
}

export const V2_EDGE_TYPES = { v2Edge: V2Edge };
